import React, { FunctionComponent } from 'react'
import { Pressable, View } from 'react-native'

import { LinearGradient } from 'expo-linear-gradient'
import styled, { useTheme } from 'styled-components/native'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { ChevronIconDirection } from '../../atoms/icons/ChevronIcon'
import { ChevronV2Icon } from '../../atoms/icons/ChevronV2Icon'
import { Image } from '../../atoms/image/Image'
import { tID } from '../../utils/utils'

export interface EssentialsPressInfo {
  url: string
  title: string
  contentType: string
}

export interface EssentialsBubbleProps {
  title: string
  url: string
  thumbnailURI: string
  articleTypeLabel: string
  readTime: string
  onEssentialsPress: (info: EssentialsPressInfo) => void
}

const PressableContainer = styled(Pressable)(({ theme }) => ({
  marginTop: theme.spacing['12px'],
  cursor: 'pointer',
}))

const GradientContainer = styled(LinearGradient)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  borderRadius: '16px',
  padding: theme.spacing['12px'],
}))

const ContentContainer = styled.View(({ theme }) => ({
  flex: 1,
  flexShrink: 1,
  marginHorizontal: theme.spacing['8px'],
}))

const Thumbnail = styled(Image)(({ theme }) => ({
  borderRadius: theme.spacing['8px'],
  height: '40px',
  width: '40px',
}))

const Title = styled(BodyText)(({ theme }) => ({
  marginBottom: theme.spacing['4px'],
}))

const MetaContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
})

const Ellipsis = styled.View(({ theme }) => ({
  width: '3px',
  height: '3px',
  borderRadius: '1.5px',
  backgroundColor: theme.colors.textSecondary,
  marginLeft: '6px',
  marginRight: '6px',
}))

export const EssentialsBubble: FunctionComponent<EssentialsBubbleProps> = ({
  title,
  url,
  thumbnailURI,
  articleTypeLabel,
  readTime,
  onEssentialsPress,
}) => {
  const { colors } = useTheme()
  const { chatBubbleReceiverAi } = colors.components.messageItem

  const handlePress = () => {
    onEssentialsPress({ url, title, contentType: articleTypeLabel })
  }

  return (
    <PressableContainer
      testID={tID('EssentialsBubble')}
      accessibilityRole='button'
      accessibilityLabel={title}
      onPress={handlePress}
    >
      <GradientContainer
        colors={[chatBubbleReceiverAi.gradientStart.background, chatBubbleReceiverAi.gradientEnd.background]}
        start={[0, 0.3]}
        end={[1, 0.7]}
      >
        {thumbnailURI && <Thumbnail source={thumbnailURI} />}
        <ContentContainer>
          <Title text={title} size={BodyTextSize.SMALL} medium numberOfLines={2} color={chatBubbleReceiverAi.text} />
          <MetaContainer>
            <BodyText text={articleTypeLabel} size={BodyTextSize.CAPTION} color={colors.textSecondary} medium />
            <Ellipsis style={{ backgroundColor: colors.textSecondary }} />
            <BodyText text={readTime} size={BodyTextSize.CAPTION} color={colors.textSecondary} medium />
          </MetaContainer>
        </ContentContainer>
        <View>
          <ChevronV2Icon size={12} direction={ChevronIconDirection.RIGHT} fillColor={colors.iconDefault} />
        </View>
      </GradientContainer>
    </PressableContainer>
  )
}
